/** @jsxRuntime classic */
/** @jsx jsx */
import { css, Global, jsx } from "@emotion/core";
import { purchaseActions } from "modules/purchase";
import { streamingActions } from "modules/streaming";
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { convertMapToValues } from "utility";
import { hasId } from "utility/modelTypeGuards";

import StreamingLiveCommerceCartV2 from "./liveCommerce/StreamingLiveCommerceCartV2";
import StreamingLiveCommerceDisplayInfo from "./liveCommerce/StreamingLiveCommerceDisplayInfo";
import StreamingLiveCommerceGoodsListV2 from "./liveCommerce/StreamingLiveCommerceGoodsListV2";
import {
  CompletePurchase,
  ErrorModal,
} from "./liveCommerce/StreamingLiveCommerceModal";

import type { ProductData } from "@spwn/types/firebase/firestore";

type Props = {
  currentVideoId: string;
  THEOPlayerElementHeight: number;
};
const StreamingLiveCommerceV2: React.FC<Props> = (props) => {
  const [openCart, setOpenCart] = React.useState<boolean>(false);
  const [openInfo, setOpenInfo] = React.useState<boolean>(false);

  const [goodsInfo, setGoodsInfo] = React.useState<ProductData | null>(null);

  const eventGoodsMap = useSelector((state) => state.ticket.eventGoodsMap);
  const eventTicketMap = useSelector((state) => state.ticket.eventTicketMap);
  const { cartItemMap, cartTotalNum, isPurchased, errorInfo } = useSelector(
    (state) => state.streaming.liveCommerce
  );

  const pseudoCart = useMemo(
    () => convertMapToValues(cartItemMap),
    [cartItemMap]
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(purchaseActions.refreshFingerprint());
    return () => {
      clearLiveCommerceCart();
      closeErrorModal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const mapEventItem = (itemData: ProductData): ProductData | undefined => {
    if (!hasId(itemData)) {
      return;
    }
    const { productType } = itemData;
    if (productType === "ticket") {
      return eventTicketMap[itemData._id];
    }

    if (productType === "goods") {
      return eventGoodsMap[itemData._id];
    }
  };

  const incrementCartItem = (itemData: ProductData) => {
    const item = mapEventItem(itemData);
    if (!item) {
      return;
    }
    dispatch(streamingActions.updateLiveCommerceCart({ item, count: 1 }));
  };

  const decrementCartItem = (itemData: ProductData) => {
    const item = mapEventItem(itemData);
    if (!item) {
      return;
    }

    dispatch(streamingActions.updateLiveCommerceCart({ item, count: -1 }));
  };

  const removeCartItem = (itemData: ProductData) => {
    const item = mapEventItem(itemData);
    if (!item) {
      return;
    }

    dispatch(
      streamingActions.updateLiveCommerceCart({
        item,
        count: 0,
        isRemove: true,
      })
    );
  };

  // TODO 修正必要(?)
  const clearLiveCommerceCart = () => {
    dispatch(purchaseActions.refreshFingerprint());
    dispatch(streamingActions.successPurchaseLiveCommerce(false));
    setOpenCart(false);
  };

  const closeErrorModal = () => {
    dispatch(
      streamingActions.toggleLiveCommerceError({
        isError: false,
        caption: "",
        msg: "",
      })
    );
  };

  const openGoodsInfo = (goodsItem: ProductData) => {
    setOpenInfo(true);
    setGoodsInfo(goodsItem);
  };

  const closeGoodsInfo = () => {
    setOpenInfo(false);
    setGoodsInfo(null);
  };

  if (!eventGoodsMap) {
    return null;
  }

  if (errorInfo.isError) {
    return (
      <ErrorModal
        caption={errorInfo.caption}
        msg={errorInfo.msg}
        closeModal={closeErrorModal}
      />
    );
  }

  if (isPurchased) {
    return <CompletePurchase clearLiveCommerceCart={clearLiveCommerceCart} />;
  }

  if (openInfo && goodsInfo) {
    return (
      <StreamingLiveCommerceDisplayInfo
        openInfo={openInfo}
        goodsInfo={goodsInfo}
        THEOPlayerElementHeight={props.THEOPlayerElementHeight}
        closeGoodsInfo={closeGoodsInfo}
      />
    );
  }

  return (
    <React.Fragment>
      <Global styles={liveCommerceModalOverrideStyles} />

      {!openCart && (
        <StreamingLiveCommerceGoodsListV2
          THEOPlayerElementHeight={props.THEOPlayerElementHeight}
          itemNum={cartTotalNum}
          pseudoCart={pseudoCart}
          openGoodsInfo={openGoodsInfo}
          openLiveCommerceCart={() => setOpenCart(true)}
          incrementCartItem={incrementCartItem}
        />
      )}

      {openCart && (
        <StreamingLiveCommerceCartV2
          currentVideoId={props.currentVideoId}
          THEOPlayerElementHeight={props.THEOPlayerElementHeight}
          pseudoCart={convertMapToValues(cartItemMap)}
          incrementCartItem={incrementCartItem}
          decrementCartItem={decrementCartItem}
          removeCartItem={removeCartItem}
          closeLiveCommerceCart={() => setOpenCart(false)}
        />
      )}
    </React.Fragment>
  );
};

// TODO: global 経由でのモーダルスタイル注入をしている。適切な方法を考えたい
const liveCommerceModalOverrideStyles = css`
  .loading_layout {
    display: flex;
    align-items: center;
    height: 100% !important;
    margin: 0px auto 0 !important;
    padding: 0px !important;
    background-color: initial !important;
    &:focus {
      outline: none;
    }
    .sk-cube-grid {
      margin: 0 auto !important;
    }
    .sk-cube {
      background-color: #ffffff !important;
    }
    h2 {
      display: none !important;
    }
    .popup_message {
      display: none !important;
    }
  }
`;

export default StreamingLiveCommerceV2;
